<template>
  <div class="sound-select" :data-testid="`sound-select-${label}-container`">
    <label :for="`sound-select-${label}`" class="spot-label">{{ label }}</label>
    <md-button
      v-if="currentSound && currentSound.preview"
      :md-ripple="false"
      aria-label="Play"
      data-testid="play-button"
      class="play-button md-icon-button md-dense"
      @click.stop="preview()"
    >
      <md-icon v-if="isPlaying()">stop</md-icon>
      <md-progress-spinner
        v-else-if="isLoading()"
        class="preview-spinner"
        :md-diameter="24"
        :md-stroke="3"
        md-mode="indeterminate"
      />
      <md-icon v-else>play_arrow</md-icon>
    </md-button>
    <md-field>
      <md-select
        v-testid="`sound-select-${label}`"
        :aria-label="`Select Sound ${label}`"
        :placeholder="`Spot ${label}`"
        :value="value"
        :disabled="disabled"
        @md-closed="stopPreview()"
        @input="value => update(value)"
        @click.native="event => event.stopPropagation()"
      >
        <md-option
          v-for="sound in orderedSounds"
          :key="sound.id"
          :aria-label="sound.resourceName.replace(/_/g, ' ')"
          class="sound-select-option"
          :label="sound.resourceName"
          :value="sound.id"
          data-testid="sound-select-option"
        >
          {{ sound.resourceName.replace(/_/g, ' ') }}
        </md-option>
      </md-select>
    </md-field>
    <md-icon class="volume-icon">volume_up</md-icon>
    <vue-slider
      class="volume-slider"
      :min="0"
      :max="10"
      :interval="1"
      :height="10"
      :dot-size="10"
      :adsorb="true"
      :value="volume * 10"
      :disabled="disabled"
      data-testid="volume-slider"
      @change="value => updateVolume(value / 10)"
    />
  </div>
</template>

<script>
import orderBy from 'lodash/orderBy';
import keyBy from 'lodash/keyBy';
import { Howl } from 'howler';
import VueSlider from 'vue-slider-component';

let globalPreviewSound = null;

export default {
  name: 'sound-select',
  components: {
    VueSlider,
  },
  props: {
    sounds: {
      type: Array,
      required: true
    },
    value: {
      type: String,
      required: true,
    },
    volume: {
      type: Number,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        volume: 1,
      },
      previewSound: null,
    };
  },
  computed: {
    orderedSounds() {
      return orderBy(this.sounds, 'resourceName');
    },
    soundsById() {
      return keyBy(this.sounds, 'id');
    },
    currentSound() {
      return this.soundsById[this.value];
    },
  },
  beforeDestroy() {
    this.stopPreview();
  },
  methods: {
    preview(sound = this.currentSound) {
      if (!sound.preview || !sound.preview.url) {
        return;
      }
      if (this.isPlaying(sound) || this.isLoading(sound)) {
        this.stopPreview();
        return;
      }
      if (this.previewSound) {
        this.stopPreview();
      }
      this.previewSound = new Howl({
        src: [sound.preview.url],
        loop: true,
        volume: this.volume,
      });

      if (globalPreviewSound) {
        globalPreviewSound.stop();
        globalPreviewSound.unload();
      }

      globalPreviewSound = this.previewSound;
      this.previewSound.once('loaderror', () => {
        this.previewSound.unload();
      });
      this.previewSound.play();
    },
    stopPreview() {
      if (this.previewSound) {
        this.previewSound.stop();
      }
    },
    isPlaying(sound = this.currentSound) {
      return (
        this.isActivePreview(sound) &&
        this.previewSound &&
        this.previewSound.playing()
      );
    },
    isLoading(sound = this.currentSound) {
      return (
        this.isActivePreview(sound) &&
        this.previewSound &&
        this.previewSound.state() === 'loading'
      );
    },
    isActivePreview(sound = this.currentSound) {
      return (
        sound.preview &&
        this.previewSound &&
        this.previewSound._src === sound.preview.url
      );
    },
    update(value) {
      this.$emit('input', value);
    },
    updateVolume(volume) {
      if (this.previewSound) {
        this.previewSound.volume(volume);
      }
      this.$emit('changeVolume', volume);
    },
  },
};
</script>

<style scoped>
.sound-select {
  padding: 5px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
}

.spot-label {
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0px 0px 6px rgba(0,0,0,0.5);
}

.md-field {
  flex: 3;
}

.md-icon-font {
  color: unset !important;
  text-shadow: 0px 0px 6px rgba(0,0,0,0.5);
}

.play-button {
  color: white;
}

.volume-icon {
  width: 16px;
  height: 16px;
  font-size: 16px !important;
}

.volume-slider {
  flex: 1;
  margin-left: 5px;
}

.preview-spinner {
  width: 24px;
  height: 24px;
}

</style>

<style>
.sound-select-option .md-list-item-text {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.sound-select-option .md-list-item-text .md-button .md-ripple {
  padding: 0;
}
</style>
